import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { getSummaryTaskStatus } from '../../../../api'
import { EMPTY_OBJECT } from '../../../../constants/common'
import { useAsyncTask, useNotification } from '../../../../hooks'
import {
  getAsyncTaskId,
  isAsyncTaskFinished,
  isAsyncTaskSuccessStatus,
} from '../../../../utils/async'
import { PROFILE_AI_SUMMARY_WIDGET_WARNING } from '../../ProfileAISummaryWidget'

import { ASYNC_TASK_INTERVAL } from './useWidgetAISummaryLoader.constants'
import { isNotSummarizableError } from './useWidgetAISummaryLoader.utils'

export function useWidgetAISummaryLoader({
  asyncInterval = ASYNC_TASK_INTERVAL,
  onError,
  onGenerateSummary,
  onGetTaskStatus = getSummaryTaskStatus,
  onReloadData,
  onSuccess,
} = EMPTY_OBJECT) {
  const { handleAddErrorNotification } = useNotification()

  const { handleAddAsyncTask, handleRemoveAsyncTask } = useAsyncTask()

  const [loading, setLoading] = useState(false)

  // Id of the polling task currently owned by this widget. Used to stop the
  // interval on unmount — the task lives in the app-level AsyncTaskProvider, so
  // it would otherwise keep polling after the widget is gone.
  const activeTaskIdRef = useRef(null)

  const handleRemoveAsyncTaskRef = useRef(handleRemoveAsyncTask)

  useLayoutEffect(() => {
    handleRemoveAsyncTaskRef.current = handleRemoveAsyncTask
  })

  function handleTaskFail() {
    onError({
      can_generate: false,
      warnings: [PROFILE_AI_SUMMARY_WIDGET_WARNING.temporaryUnavailable],
    })
  }

  function removeTask(task) {
    handleRemoveAsyncTask(task)

    if (activeTaskIdRef.current === task) {
      activeTaskIdRef.current = null
    }
  }

  async function handleTaskSuccess(data) {
    if (data.result) {
      setLoading(false)
      onSuccess({ ...data.result, can_generate: false })

      return
    }

    if (!onReloadData) {
      setLoading(false)
      handleTaskFail()

      return
    }

    try {
      const { data: reloadedData } = await onReloadData()

      setLoading(false)
      onSuccess({ ...reloadedData.email_summary, can_generate: false })
    } catch {
      setLoading(false)
      handleTaskFail()
    }
  }

  async function checkTaskStatus(task) {
    try {
      const { data } = await onGetTaskStatus({ id: task })

      if (!isAsyncTaskFinished(data)) {
        return
      }

      // Stop polling before handling the result — the success path may await a
      // reload, and we must not fire another poll in the meantime.
      removeTask(task)

      if (isAsyncTaskSuccessStatus(data)) {
        await handleTaskSuccess(data)
      } else {
        setLoading(false)
        handleTaskFail()
      }
    } catch {
      setLoading(false)
      handleTaskFail()
      removeTask(task)
      handleAddErrorNotification('Error.CommonApiRequestFailKey')
    }
  }

  async function handleLoad(data) {
    setLoading(true)

    try {
      const { data: generatedData } = await onGenerateSummary(data)
      const asyncTask = getAsyncTaskId(generatedData)

      activeTaskIdRef.current = asyncTask

      handleAddAsyncTask({
        id: asyncTask,
        interval: setInterval(() => {
          checkTaskStatus(asyncTask)
        }, asyncInterval),
      })
    } catch (error) {
      setLoading(false)

      if (isNotSummarizableError(error)) {
        onError({
          can_generate: false,
          warnings: [PROFILE_AI_SUMMARY_WIDGET_WARNING.notSummarizable],
        })
      } else {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      }
    }
  }

  useEffect(
    () => () => {
      if (activeTaskIdRef.current != null) {
        handleRemoveAsyncTaskRef.current(activeTaskIdRef.current)
      }
    },
    []
  )

  return { handleLoad, loading }
}
